import { Box, type SxProps, type Theme, Typography } from '@mui/material';
import { Show } from '@side/pantry';
import Image from 'next/image';
import { Fragment } from 'react';
import { getPlaceholderImage } from '@/common/utils/coverPhoto';

export const THUMB_WIDTH = 88;
export const THUMB_HEIGHT = 56;
export const CARD_PHOTO_ASPECT = '320 / 150';

type CoverPhotoProps = {
  coverPhotoUrl: string | null;
  id: string;
  sizes: string;
  sx: SxProps<Theme>;
};

export const CoverPhoto = ({
  coverPhotoUrl,
  id,
  sizes,
  sx,
}: CoverPhotoProps) => (
  <Box
    sx={[
      {
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'grey.100',
      },
      !coverPhotoUrl && {
        border: '0.5px solid',
        borderColor: 'grey.500',
        backgroundColor: 'common.white',
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <Show
      when={coverPhotoUrl}
      fallback={
        <Image
          src={getPlaceholderImage(id)}
          alt=""
          fill
          sizes={sizes}
          style={{ objectFit: 'cover' }}
        />
      }
    >
      {(url) => (
        // Cover photos are GCS signed URLs, which are intentionally excluded
        // from the next/image host allow-list, so render with a plain <img>
        // (same approach as the TLV AddressCell).
        <Box
          component="img"
          src={url}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
    </Show>
  </Box>
);

type DetailLineParts = {
  interactionStatus: string;
  mlsNumber?: string | null;
  formattedSellers: string;
};

export const ListingDetailLine = ({
  interactionStatus,
  mlsNumber,
  formattedSellers,
}: DetailLineParts) => {
  const parts = [
    interactionStatus,
    mlsNumber && `MLS #: ${mlsNumber}`,
    formattedSellers && `Seller: ${formattedSellers}`,
  ].filter((part): part is string => !!part);

  return (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {parts.map((part, index) => (
        <Fragment key={part}>
          {index > 0 && (
            <Box component="span" aria-hidden sx={{ color: 'divider', mx: 1 }}>
              |
            </Box>
          )}
          <Box component="span">{part}</Box>
        </Fragment>
      ))}
    </Typography>
  );
};
